import { useCallback, useEffect, useRef, useState } from 'react'
import useEmblaCarousel from 'embla-carousel-react'
import EmblaCarouselFade from 'embla-carousel-fade'
import EmblaCarouselAutoplay from 'embla-carousel-autoplay'

import { Button } from '#/components/ui/button'
import { Link } from '@tanstack/react-router'
import bismillah from '#/assets/bismillah.svg'
import heroImgOutline from '#/assets/hero-img-outline.svg'
import { HERO_CTA_LABEL, HERO_SLIDES } from '#/data/homeHero'

import { RevealItem, ScrollReveal } from '#/components/common/ScrollReveal'
import { FaRegPlayCircle } from 'react-icons/fa'
import { TextAnimate } from '#/components/common/TextAnimate'

export default function HeroSection() {
  const autoplay = useRef(
    EmblaCarouselAutoplay({
      delay: 5000,
      stopOnInteraction: false,
      stopOnMouseEnter: true,
    }),
  )
  const fade = useRef(EmblaCarouselFade())
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true }, [
    fade.current,
    autoplay.current,
  ])
  const [selectedIndex, setSelectedIndex] = useState(0)

  const onSelect = useCallback(() => {
    if (!emblaApi) {
      return
    }

    setSelectedIndex(emblaApi.selectedScrollSnap())
  }, [emblaApi])

  useEffect(() => {
    if (!emblaApi) {
      return
    }

    onSelect()
    emblaApi.on('select', onSelect)
    emblaApi.on('reInit', onSelect)

    return () => {
      emblaApi.off('select', onSelect)
      emblaApi.off('reInit', onSelect)
    }
  }, [emblaApi, onSelect])

  const activeSlide = HERO_SLIDES[selectedIndex] ?? HERO_SLIDES[0]

  return (
    <section
      className="relative  min-h-220 w-full flex flex-col justify-center    "
      id="home"
    >
      <div className="container mx-auto flex-col items-center flex">
        <div className="hero-mask absolute inset-0 z-0">
          {HERO_SLIDES.map((slide, index) => (
            <picture
              key={slide.title}
              aria-hidden="true"
              className={`pointer-events-none absolute inset-0 z-0 block size-full transition-opacity duration-700 ${
                index === selectedIndex ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <source
                media="(max-width: 768px)"
                srcSet={slide.backgroundMobile}
                width={768}
                height={1376}
              />
              <img
                alt=""
                className="size-full object-cover"
                src={slide.background}
                loading={index === 0 ? 'eager' : 'lazy'}
                fetchPriority={index === 0 ? 'high' : 'low'}
                width={1600}
                height={900}
              />
            </picture>
          ))}
          <div className="absolute inset-0 z-10 bg-primary-900/80"></div>
        </div>

        <div className="absolute bottom-10.75 left-1/2 flex -translate-x-1/2 items-center gap-1.5 overflow-clip">
          {HERO_SLIDES.map((_, index) => (
            <button
              key={`hero-indicator-${index}`}
              type="button"
              aria-label={`Go to slide ${index + 1}`}
              onClick={() => {
                emblaApi?.scrollTo(index)
                autoplay.current?.reset()
              }}
              className="h-6 w-10 flex justify-center items-center  cursor-pointer "
            >
              <span
                className={`h-0.5 w-10 transition-colors ${
                  index === selectedIndex ? 'bg-primary-500' : 'bg-white'
                }`}
              ></span>
            </button>
          ))}
        </div>

        <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 w-full items-center gap-10 lg:py-12.5 py-30 max-lg:flex-col max-lg:items-start max-lg:gap-8">
          <ScrollReveal
            margin="0px"
            delayChildren={0}
            stagger={0.06}
            className="flex max-lg:items-center flex-1 flex-col items-start justify-center gap-10 max-lg:text-center"
          >
            <div className="flex w-full flex-col max-lg:items-center items-start gap-4">
              <RevealItem className="w-full flex max-lg:justify-center">
                <img
                  alt=""
                  className="w-45 max-sm:h-15 max-sm:w-35"
                  src={bismillah}
                  width={180}
                  height={67}
                />
              </RevealItem>
              <div className="w-full">
                <TextAnimate
                  by="line"
                  animation="blurInUp"
                  as="h1"
                  className="xl:text-[60px] text-5xl font-bold text-white max-lg:w-full max-md:text-[42px] max-sm:text-[36px]"
                >
                  {activeSlide.title}
                </TextAnimate>
              </div>
              <div className="w-full">
                <TextAnimate
                  by="word"
                  animation="fadeIn"
                  duration={1}
                  className="text-[18px] font-normal text-neutral-50 max-lg:w-full max-sm:text-[16px]"
                >
                  {activeSlide.description}
                </TextAnimate>
              </div>
            </div>

            <RevealItem className="w-full flex max-lg:justify-center">
              <Link to="/subscribe">
                <Button>
                  <FaRegPlayCircle />
                  {HERO_CTA_LABEL}
                </Button>
              </Link>
            </RevealItem>
          </ScrollReveal>

          <ScrollReveal
            className="max-w-120 lg:max-w-140 mx-auto relative"
            stagger={0.1}
          >
            <RevealItem>
              <div ref={emblaRef} className="overflow-hidden">
                <div className="flex">
                  {HERO_SLIDES.map((slide, index) => (
                    <div
                      key={slide.title}
                      className="relative flex-[0_0_100%] will-change-opacity"
                    >
                      <div className="relative overflow-hidden">
                        <img
                          alt={slide.foregroundAlt}
                          className="hero-img-mask object-cover object-top aspect-square w-full"
                          decoding="async"
                          loading={index === 0 ? 'eager' : 'lazy'}
                          fetchPriority={index === 0 ? 'high' : 'low'}
                          width={500}
                          height={500}
                          src={slide.foreground}
                        />
                        <img
                          alt=""
                          aria-hidden="true"
                          className="absolute inset-0 w-full h-full"
                          decoding="async"
                          loading={index === 0 ? 'eager' : 'lazy'}
                          width={500}
                          height={500}
                          src={heroImgOutline}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </RevealItem>
          </ScrollReveal>
        </div>
      </div>
    </section>
  )
}
